import React, { FormEvent, useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Toolbar from '@material-ui/core/Toolbar';

import { useAppState } from '../../state';
import { useParams } from 'react-router-dom';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import MediaErrorSnackbar from './MediaErrorSnackbar/MediaErrorSnackbar';

export enum Steps {
  roomNameStep,
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: theme.palette.background.default,
    },
    toolbar: {
      [theme.breakpoints.down('xs')]: {
        padding: 0,
      },
    },
    rightButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    form: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        marginLeft: '2.2em',
      },
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      maxWidth: 200,
    },
    loadingSpinner: {
      marginLeft: '1em',
    },
    displayName: {
      margin: '1.1em 0.6em',
      minWidth: '200px',
      fontWeight: 600,
    },
    joinButton: {
      margin: '1em',
    },
  })
);

export default function PreJoinScreens() {
  const { user } = useAppState();
  const classes = useStyles();
  const { URLRoomName } = useParams();
  const { getToken, isFetching } = useAppState();
  const { isConnecting, connect, getAudioAndVideoTracks, localTracks } = useVideoContext();
  const [mediaError, setMediaError] = useState<Error>();
  const [disabled, setDisabled] = useState<boolean>(true);

  const [name] = useState<string>(user?.displayName || '');
  const [roomName, setRoomName] = useState<string>('');

  useEffect(() => {
    if (URLRoomName) {
      setRoomName(URLRoomName);
    }
  }, [URLRoomName]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDisabled(true);
    // If this app is deployed as a twilio function, don't change the URL because routing isn't supported.
    const redirect = process.env.REACT_APP_REDIRECT || 'true';
    if (redirect === 'true' && !window.location.origin.includes('twil.io')) {
      window.history.replaceState(null, '', window.encodeURI(`/room/${roomName}${window.location.search || ''}`));
    }
    getToken(name, roomName)
      // @ts-ignore
      .then(token => connect(token))
      .catch(() => {
        /* do nothing */
      });
  };

  useEffect(() => {
    if (!mediaError) {
      getAudioAndVideoTracks().catch(error => {
        console.log('Error acquiring local media:');
        console.dir(error);
        setMediaError(error);
      });
    }
  }, [getAudioAndVideoTracks, mediaError]);

  useEffect(() => {
    if (!isConnecting && !isFetching && localTracks.length > 0) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [isConnecting, isFetching, localTracks]);

  return (
    <>
      <MediaErrorSnackbar error={mediaError} />
      <AppBar className={classes.container} position="static">
        <Toolbar className={classes.toolbar}>
          <form className={classes.form} onSubmit={handleSubmit}>
            <Button
              className={classes.joinButton}
              type="submit"
              color="primary"
              variant="contained"
              disabled={disabled}
            >
              Join Room
            </Button>
            {disabled && !mediaError && <CircularProgress className={classes.loadingSpinner} />}
          </form>
        </Toolbar>
      </AppBar>
    </>
  );
}
